import React from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import Icon from '@mdi/react';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import {mdiCashRegister, mdiTablet, mdiCreditCardOutline, mdiPrinterPos, mdiPuzzle} from '@mdi/js';
import POSState from 'features/POSRegisters/interfaces/POSState';
import Tabs from 'features/POSRegisters/POSTable/Registers/interfaces/Tabs';
import Device from 'features/POSRegisters/interfaces/Device';
import ScanDetails from 'features/POSRegisters/interfaces/ScanDetails';
import {SeverityPriorityEnum} from 'features/POSRegisters/interfaces/Severity';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import {
  checkIsScanFailureAlert,
  checkIsOrphanDeviceAlert,
  checkIsConfigurationDeviceAlert,
} from 'features/POSRegisters/functions/alertTypes';

//#region Constants
export const tabsDictionary: Record<string, Tabs> = {
  SUMMARY: Tabs.SUMMARY,
  IPAD_AIR: Tabs.IPAD_AIR,
  IPAD_MINI: Tabs.IPAD_MINI,
  RECEIPT_PRINTER: Tabs.RECEIPT_PRINTER,
  PAYMENT_TERMINAL: Tabs.PAYMENT_TERMINAL,
  ANOMALIES: Tabs.ANOMALIES,
};
//#endRegion Constants

//#region styles
const StyledTabContainer = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  margin-top: 18px;
  padding: 0px 0px 0px 0px;
  background: transparent;
  border-bottom: 1px solid ${props => props.theme.grey};
`;

const StyledTab = styled(Button)`
  display: flex;
  padding: 4px 8px;
  color: ${props => props.theme.grey};
  text-decoration: none;
  font-family: 'Trade Gothic Bold Condensed';
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
  border-bottom: 6px solid transparent;
  border-radius: 0;
  transition: 250ms;
  .MuiButton-label {
    margin-bottom: 5px;
    gap: 5px;
  }
  &.MuiButton-root {
    line-height: 17px;
  }
  &:hover {
    text-decoration: none;
    color: ${props => props.theme.altText};
    border-bottom: 6px solid ${props => props.theme.navBlue};
  }
  &.active {
    color: ${props => props.theme.altText};
    border-bottom: 6px solid ${props => props.theme.navBlue};
  }
  &.hidden {
    display: none;
  }
`;

const StyledIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  height: 24px;
  width: 24px;
`;

const StyledAlertsNotification = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
`;
//#end region styles

interface HeaderTabsProps {
  selectedTab: string;
  handleTabChange: (tab: Tabs) => void;
  state: POSState;
}

function HeaderTabs({state, selectedTab, handleTabChange}: HeaderTabsProps) {
  const {ipads, ipadsMini, receiptPrinters, barcodeScanners, cashDrawers, paymentTerminals} = state;

  const handleClick = (tab: Tabs) => {
    handleTabChange(tab);
  };

  const hasIssues = (devices: Device<ScanDetails>[]) =>
    devices.some(device =>
      device?.alerts?.some(
        alert => Number(SeverityPriorityEnum[alert.siteImpact?.toUpperCase() as any]) > 1
      )
    );

  const devices = [
    ...state.ipads?.map(device => ({...device, type: 'ipad' as DeviceType})),
    ...state.ipadsMini?.map(device => ({...device, type: 'ipadMini' as DeviceType})),
    ...state.receiptPrinters?.map(device => ({...device, type: 'receiptPrinter' as DeviceType})),
    ...state.cashDrawers?.map(device => ({...device, type: 'cashDrawer' as DeviceType})),
    ...state.barcodeScanners?.map(device => ({...device, type: 'barcodeScanner' as DeviceType})),
    ...state.paymentTerminals?.map(device => ({...device, type: 'paymentTerminal' as DeviceType})),
  ].filter(device => device?.alerts?.length);

  const scanFailureAlerts = devices.map(device => ({
    ...device,
    alerts: device?.alerts?.filter(alert => checkIsScanFailureAlert(alert)),
  }));

  const orphanDeviceAlerts = devices.map(device => ({
    ...device,
    alerts: device?.alerts?.filter(alert => checkIsOrphanDeviceAlert(alert)),
  }));

  const configurationDeviceAlerts = devices.map(device => ({
    ...device,
    alerts: device?.alerts?.filter(alert => checkIsConfigurationDeviceAlert(alert)),
  }));

  return (
    <StyledTabContainer>
      <StyledTab
        className={'summary' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.SUMMARY)}
      >
        <StyledIconContainer>
          <Icon path={mdiCashRegister} size={0.875} />
        </StyledIconContainer>
        Summary
      </StyledTab>
      <StyledTab
        className={'ipad-air' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.IPAD_AIR)}
      >
        <StyledIconContainer>
          <Icon path={mdiTablet} size={1} />
          <StyledAlertsNotification>
            {hasIssues(ipads) && <SeverityBadge severity={getPriorityAlert(ipads)} size={0.75} />}
          </StyledAlertsNotification>
        </StyledIconContainer>
        Ipad Air
      </StyledTab>
      <StyledTab
        className={'ipad-mini' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.IPAD_MINI)}
      >
        <StyledIconContainer>
          <Icon path={mdiTablet} size={0.75} />
          <StyledAlertsNotification>
            {hasIssues(ipadsMini) && (
              <SeverityBadge severity={getPriorityAlert(ipadsMini)} size={0.75} />
            )}
          </StyledAlertsNotification>
        </StyledIconContainer>
        Ipad Mini (CFD)
      </StyledTab>
      <StyledTab
        className={'receipt-printer' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.RECEIPT_PRINTER)}
      >
        <StyledIconContainer>
          <Icon path={mdiPrinterPos} size={1} />
          <StyledAlertsNotification>
            {hasIssues([...receiptPrinters, ...barcodeScanners, ...cashDrawers]) && (
              <SeverityBadge
                severity={getPriorityAlert([
                  ...receiptPrinters,
                  ...barcodeScanners,
                  ...cashDrawers,
                ])}
                size={0.75}
              />
            )}
          </StyledAlertsNotification>
        </StyledIconContainer>
        Receipt Printer
      </StyledTab>
      <StyledTab
        className={'payment-terminal' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.PAYMENT_TERMINAL)}
      >
        <StyledIconContainer>
          <Icon path={mdiCreditCardOutline} size={1} />
          <StyledAlertsNotification>
            {hasIssues(paymentTerminals) && (
              <SeverityBadge severity={getPriorityAlert(paymentTerminals)} size={0.75} />
            )}
          </StyledAlertsNotification>
        </StyledIconContainer>
        Payment Terminal
      </StyledTab>
      <StyledTab
        className={`${'anomalies' === selectedTab ? 'active' : ''} ${
          !hasIssues([...scanFailureAlerts, ...orphanDeviceAlerts, ...configurationDeviceAlerts]) &&
          'hidden'
        }`}
        onClick={() => handleClick(Tabs.ANOMALIES)}
      >
        <StyledIconContainer>
          <Icon path={mdiPuzzle} size={1} />
          <StyledAlertsNotification>
            {hasIssues([
              ...scanFailureAlerts,
              ...orphanDeviceAlerts,
              ...configurationDeviceAlerts,
            ]) && (
              <SeverityBadge
                severity={getPriorityAlert([
                  ...scanFailureAlerts,
                  ...orphanDeviceAlerts,
                  ...configurationDeviceAlerts,
                ])}
                size={0.75}
              />
            )}
          </StyledAlertsNotification>
        </StyledIconContainer>
        Anomalies
      </StyledTab>
    </StyledTabContainer>
  );
}

export default HeaderTabs;
